import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import { queryKeys } from "../../lib/queryKeys";
import { getCareManagementQuery } from "../../services";
import type { CareManagementQueryResponse } from "../../types/care-management";

/**
 * Treat empty / unassigned / partial payloads as "no data" so tab UIs can
 * show an empty state instead of throwing on missing review/case trees.
 */
export const normalizeCareManagementQuery = (
  payload: CareManagementQueryResponse | null | undefined
): CareManagementQueryResponse | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (payload.exists === false || !payload.review) {
    return null;
  }

  return payload;
};

export const useCareManagementQuery = (
  mrn: string,
  providerNpi: string,
  options?: { polling?: boolean; enabled?: boolean }
) => {
  const polling = options?.polling ?? false;
  const enabled = options?.enabled ?? Boolean(mrn && providerNpi);

  return useQuery({
    queryKey: queryKeys.careManagementQuery(mrn, providerNpi),
    queryFn: async () => {
      const response = await getCareManagementQuery({ mrn, providerNpi });
      const raw = response?.data as
        | CareManagementQueryResponse
        | { data?: CareManagementQueryResponse }
        | null
        | undefined;

      // Support both `{ data: payload }` and a flat decrypted payload.
      const payload =
        raw && typeof raw === "object" && "data" in raw && raw.data
          ? raw.data
          : (raw as CareManagementQueryResponse | null | undefined);

      return normalizeCareManagementQuery(payload);
    },
    enabled: enabled && Boolean(mrn && providerNpi),
    placeholderData: keepPreviousData,
    refetchInterval: polling ? POLLING_INTERVAL_MS : false,
  });
};
